import type { GoalieChangeEdit, GoalieChangeRow } from '../types/goalieChange'

/** The API answers an edit with the whole row, which always carries its id. */
const isFullRow = (
  update: GoalieChangeRow | GoalieChangeEdit
): update is GoalieChangeRow => typeof update.id === 'string'

const withoutUndefined = <T extends object>(input: T): Partial<T> => {
  const output: Partial<T> = {}
  ;(Object.keys(input) as Array<keyof T>).forEach((key) => {
    if (input[key] !== undefined) {
      output[key] = input[key]
    }
  })
  return output
}

/**
 * The row to store after a goalie change is edited.
 *
 * The API's answer REPLACES the stored row. It leaves a goalie key out when
 * that side of the change is an empty net, and a merge cannot remove a key, so
 * merging would keep the goalie the edit just took out.
 *
 * An edit made while offline carries only what the scorer touched, with every
 * other field `undefined`. It is laid over the stored row and its `undefined`
 * fields are ignored, so a clock-only edit keeps both goalies. `null` is a
 * value: it means that side is now an empty net.
 */
export const applyGoalieChangeUpdate = (
  stored: GoalieChangeRow,
  update: GoalieChangeRow | GoalieChangeEdit
): GoalieChangeRow => {
  if (isFullRow(update)) {
    return update
  }

  return { ...stored, ...withoutUndefined(update) }
}
